import { html, svg, noChange } from "lit-html";
import { Directive, directive } from "lit-html/directive.js";
import { isMobile, isEmbedded } from "./mediaQuery.js";

let [ lang ] = document.documentElement.lang.split("-");

function localizeProfile(profile) {
  if (lang == "es") {
    return profile.replace("guide/", "guide/es/");
  }
  return profile;
}

let openOnDesktop = directive(class extends Directive {

  toggled = false;

  handleEvent(e) {
    this.toggled = true;
  }

  update(part, args) {
    if (isEmbedded) return noChange;
    if (isMobile.matches) return noChange;
    if (this.toggled) return noChange;
    part.element.addEventListener("toggle", this);
    part.element.setAttribute("open", true);
  }
});

function e(key) {
  return window.STRINGS[key] || key;
}

function fi(condition, ok, fail = null) {
  return condition ?
    typeof ok == "function" ? ok() : ok
    : typeof fail == "function" ? fail() : fail;
}

function* range(start, end, step = 1) {
  for (let i = start; i <= end; i += step) {
    yield i;
  }
}

function enrollmentChart(yearly) {
  let data = [];
  let max = 0;
  for (let y of range(2010, 2026)) {
    let count = yearly[y];
    if (count) {
      max = Math.max(count, max);
      data.push([y, count]);
    }
  }
  max = max * 1.1;
  let start = data[0][0];
  let d = data.map((coords, i) => `${i ? "L" : "M"}${coords[0]},${max - coords[1]}`).join(" ");
  let area = d + ` L2026,${max} L${start},${max} Z`;
  return html`
<div class="enrollment-chart">
  <div class="first-year">${start}: ${data[0][1].toLocaleString()}</div>
  <svg viewBox="${start} 0 ${2026 - start} ${max}" preserveAspectRatio="none">
    ${[...range(Math.ceil(start / 5) * 5, 2020, 5)].map(y => svg`
    <line x1=${y} x2=${y} y1=0 y2=${max} class="grid">
    `)}
    <path class="top-line" d="${d}" />
    <path class="area-fill" d="${area}" />
  </svg>
  <div class="last-year">2026: ${yearly[2026].toLocaleString()}</div>
  `
}

const DEMO_KEY = html`
<ul class="demographic-key">
  ${"asian black hispanic multiracial white".split(" ").map(d => html`
  <li>
    <i class="dot" style="--variable: var(--${d})"></i>
    ${e(d)}
  `)}
</ul>
`;

function demographicChart(population) {
  let demos = "asian black hispanic white multiracial other".split(" ");
  let { total } = population;
  let segments = [];
  let sum = 0;
  for (let d of demos) {
    let percent = (population[d] || 0) / total * 100;
    if (percent) segments.push(html`
      <div class="segment" style="width: ${percent}%; --variable: var(--${d});">
        <div class="tooltip ${sum < 50 ? "right" : "left"}">${e(d)}: ${percent.toFixed(1)}%</div>
      </div>
    `);
    sum += percent;
  }
  if (sum < 100) {
    let percent = 100 - sum;
    segments.push(html`
      <div class="segment" style="width: ${percent}%; --variable: var(--other);">
        <div class="tooltip left">${e("other")}: ${percent.toFixed(1)}%</div>
      </div>
    `);
  }
  return html`
  <div class="demographic-bars">
    ${segments}
  </div>
  `
}

function school(state) {
  let school = state.selectedSchool;
  return html`
<h2>${school.name}</h2>
<h4 class="category">${e(school.type)} ${e(school.category)}</h4>
<h3 class="isbe">${e`schoolDesignation`}: ${e(school.isbeDesignation) || e`noDesignation`}
${fi(state.enrollment && state.enrollment[school.id], () => html`
  <hr>
  <h3>${e`schoolEnrollmentTrend`}</h3>
  ${enrollmentChart(state.enrollment[school.id].yearly)}
  <details ${openOnDesktop()}>
    <summary>
      ${e`enrollmentDemoHeader`}
    </summary>
    <div class="inner">
      <h3>${e`schoolDemographics`}</h3>
      ${DEMO_KEY}
      ${demographicChart(state.enrollment[school.id].current)}
      ${fi(state.districts && school.subdistrict, () => html`
        <hr>
        <h3>${e`districtEnrollmentDemographics`}</h3>
        ${demographicChart(state.districts[school.subdistrict].current)}
        <h3>${e`districtCensusDemographics`}</h3>
        ${demographicChart(state.districts[school.subdistrict].census)}
      `)}
    </div>
  </details>
`)}
`;
}

function district(state) {
  let district = state.districts[state.districtNumber];
  let schools = Object.values(state.profiles || {}).filter(p => p.subdistrict == state.districtNumber);
  let candidates = district.candidates.sort((a, b) => a.order - b.order);
  let profileLink = district["profile_" + lang] || district.profile_en;
  let profileText = e`profileLink`.replace("%DISTRICT%", state.districtNumber.toUpperCase());
  return html`
<h2>
  ${e`district`} ${state.districtNumber.toUpperCase()}
</h2>
<a target="_new" href="${profileLink}">${profileText}</a>
<details name="district-details" ${openOnDesktop()}>
  <summary>${e`districtCandidates`}:</summary>
  <ul class="candidates">
    ${candidates.map(c => html`
    <li>
      <a target="${c.profile ? `_new` : ``}" href="${localizeProfile(c.profile) || `javascript:void(0)`}">
        ${fi(c.photo, html`
        <img alt="" class="mug ${c.removed ? "removed" : "present"}" src="${c.photo}">
        `, html`
        <svg class="mug placeholder" viewBox="0 0 10 10">
          <circle cx=5 cy=11 r=5 class="body" />
          <circle cx=5 cy=4 r=3 class="head" />
        </svg>
        `)}
        <span class="name">${c.name}
          <span class="status">${fi(c.incumbent || c.writein || c.removed,
            `(${[ c.incumbent && e("incumbent"), c.writein && e("writeIn"), e(c.removed) ].filter(s => s).join(", ")})`
          )}
          </span>
        </span>
      </a>
    `)}
  </ul>
</details>
<details name="district-details">
  <summary>${e`countHeader`}</summary>
  <div class="inner">
    <ul class="district-counts">
      <li> ${e`schoolCount`}: <b>${schools.length}</b>
      <li> ${e`censusChildCount`}: <b>${district.census.children.toLocaleString()}</b>
      <li> ${e`enrolledCount`}: <b>${district.current.total.toLocaleString()}</b>
      <li> ${e`localElementaryStudents`}: <b>${district.zonedES.percentage}</b>
      <li> ${e`localHSStudents`}: <b>${district.zonedHS.percentage || "0.00%"}</b>
    </ul>
  </div>
</details>
<details name="district-details">
  <summary>
    ${e`enrollmentDemoHeader`}
  </summary>
  <div class="inner">
    <h3>${e`districtEnrollmentDemographics`}</h3>
    ${DEMO_KEY}
    ${demographicChart(district.current)}
    <h3>${e`districtCensusDemographics`}</h3>
    ${demographicChart(district.census)}
  </div>
</details>
  `
}

export default function(state) {
  if (state.selectedSchool) return school(state);
  if (state.districtNumber) return district(state);
}